import { FormlyFieldConfig } from '@ngx-formly/core';
import { Options } from '@rp3e11/ngx-slider';
import { formatFormlySliderFieldLabel } from 'src/app/shared/helpers/helpers';
import { StepTypes } from '../interfaces/stepGraph';
import { v4 as createId } from 'uuid';

export type StudioInputType = 'input' | 'number' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'multicheckbox' | 'date' | 'time'
    | 'datetime' | 'repeat-section'| 'accordion' | 'group' | 'array' | 'file' | 'multifile' | 'autocomplete' | 'multiautocomplete' | 'slider' | 
    'json' | 'drag-and-drop' | 'years' | 'tables' | 'columns' | 'integrationName' | 'dropdownList';

export type StudioInputTypeNew = 'TEXT' | 'NUMBER' | 'TEXTAREA' | 'SELECT' | 'RADIO' | 'CHECKBOX' | 'MULTICHECKBOX' | 'DATE' | 'TIME'
    | 'DATETIME' | 'REPEAT_SECTION'| 'ACCORDION' | 'GROUP' | 'ARRAY' | 'FILE' | 'MULTIFILE' | 'AUTOCOMPLETE' | 'MULTIAUTOCOMPLETE' | 'SLIDER' |
    'JSON' | 'DRAG_AND_DROP' | 'YEARS' | 'TABLES' | 'COLUMNS' | 'INTEGRATION_NAME' | 'RADIO_BUTTON' | 'DROPDOWN' | 'HISTORY_PICKER' | 
    'ACCORDION_GROUP' | 'TABLE_SELECT' | 'COLUMN_SELECT' | 'FIXED_VALUES_SLIDER' | 'DROPDOWN_LIST' | 'CODE_EDITOR' | 'ENTITY_SELECT' | 'OAUTH';

// variable name for field types that require entry in variable pool ?
export const studioFormInputTypesWithVariablePool = [
    'DROPDOWN_LIST',
    'RADIO_BUTTON',
    'CHECKBOX',
    'TABLE_SELECT',
    'COLUMN_SELECT',
    'FIXED_VALUES_SLIDER'
]

export const variablePoolPaths = [
    'labelsJsonPath',
    'valuesJsonPath',
    'tablesJsonPath',
    'columnsJsonPath',
]

export type StudioFormConfig = FormlyFieldConfig & {
    studioInputType?: StudioInputTypeNew;
    studioInputName?: string;
    studioInputId?: string;
    studioTemp?: boolean;
    studioIcon?: string;
    fieldGroup?: StudioFormConfig[];
    dynamicRepeatParams?: any,
    studioFieldGroup?: StudioFormConfig[];
    allowedStepTypes?: StepTypes[] | 'any' | '*' | 'none';
    isNew?: boolean;
    isEditable?: boolean;
}

export type StudioFormConfigArray = StudioFormConfig[];

export const studioFormInputIntegrationName: StudioFormConfig = {
    studioInputType: 'INTEGRATION_NAME',
    studioInputName: 'Integration Name',
    studioIcon: 'text_fields',
    studioInputId: '',
    studioTemp: false,
    type: 'input',
    key: 'input',
    isEditable: true,
    props: {
        type: 'text',
        label: 'Integration Name',
        placeholder: 'Integration Name',
        description: 'Enter the name of the integration',
        required: false,
        disabled: true,
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputText: StudioFormConfig = {
    studioInputType: 'TEXT',
    studioInputName: 'Text field',
    studioIcon: 'text_fields',
    studioInputId: '',
    studioTemp: false,
    type: 'input',
    key: 'input',
    isEditable: true,
    props: {
        type: 'text',
        label: 'Text Label',
        placeholder: 'Text Placeholder',
        description: 'Text Description',
        required: false,
        disabled: true,
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputTextArea: StudioFormConfig = {
    studioInputType: 'TEXTAREA',
    studioInputName: 'Textarea',
    studioIcon: 'short_text',
    studioInputId: '',
    studioTemp: false,
    type: 'textarea',
    key: 'input',
    isEditable: true,
    props: {
        label: 'Textarea',
        placeholder: 'Textarea Placeholder',
        description: 'Textarea Description',
        required: false,
        disabled: true,
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputNumber: StudioFormConfig = {
    studioInputType: 'NUMBER',
    studioInputName: 'Number field',
    studioIcon: '123',
    studioTemp: false,
    studioInputId: '',
    type: 'input',
    key: 'input',
    isEditable: true,
    props: {
        disabled: true,
        type: 'number',
        placeholder: 'Number Placeholder',
        description: 'Number Description',
        label: 'Number Label',
        required: true
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputDatePicker: StudioFormConfig = {
    studioInputType: 'DATE',
    studioInputName: 'Date field',
    studioIcon: 'date_range',
    studioTemp: false,
    studioInputId: '',
    type: 'input',
    key: 'input',
    isEditable: true,
    props: {
        disabled: true,
        type: 'date',
        placeholder: 'Date Placeholder',
        description: 'Date Description',
        label: 'Date Label',
        required: true
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputRadio: StudioFormConfig = {
    studioInputType: 'RADIO_BUTTON',
    studioInputName: 'Radio',
    studioIcon: 'radio_button_checked',
    studioTemp: false,
    studioInputId: '',
    type: 'radio',
    key: 'input',
    isEditable: true,
    props: {
        disabled: true,
        label: 'Radio Label',
        placeholder: 'Radio Placeholder',
        description: 'Radio Description',
        required: true,
        options: [
            { label: 'Option 1', value: 'option1' },
            { label: 'Option 2', value: 'option2' },
            { label: 'Option 3', value: 'option3' }
        ],
        valueProp: 'value',
        labelProp: 'label'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputCheckbox: StudioFormConfig = {
    studioInputType: 'CHECKBOX',
    studioInputName: 'Checkbox',
    studioIcon: 'check_box',
    studioTemp: false,
    type: 'checkbox',
    studioInputId: '',
    key: 'input',
    isEditable: true,
    props: {
        disabled: true,
        label: 'Checkbox Label',
        required: true,
        options: [
            { label: 'Option 1', value: 'option1' },
            { label: 'Option 2', value: 'option2' },
            { label: 'Option 3', value: 'option3' }
        ],
        valueProp: 'value',
        labelProp: 'label',
        indeterminate: false,
        hideFieldUnderline: false,
        hideLabel: false,
        align: 'start',
        placeholder: 'Checkbox Placeholder',
        description: 'Checkbox Description',
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputDropdownList: StudioFormConfig = {
    studioInputType: 'DROPDOWN_LIST',
    studioInputName: 'Dropdown list',
    studioIcon: 'expand_more',
    studioTemp: false,
    studioInputId: '',
    type: 'select',
    key: 'input',
    isEditable: true,
    props: {
        disabled: true,
        label: 'Label',
        placeholder: 'Placeholder',
        description: 'Description',
        required: true,
        options: [
            { label: 'Option 1', value: 'option1' },
            { label: 'Option 2', value: 'option2' },
            { label: 'Option 3', value: 'option3' }
        ],
        valueProp: 'value',
        labelProp: 'label',
        indeterminate: false,
        hideFieldUnderline: false,
        hideLabel: false,
        align: 'start',
        appearance: 'legacy'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputYears: StudioFormConfig = {
    studioInputType: 'HISTORY_PICKER',
    studioInputName: 'Years',
    studioIcon: 'expand_more',
    studioTemp: false,
    studioInputId: '',
    type: 'select',
    key: 'input',
    isEditable: true,
    props: {
        disabled: true,
        label: 'History picker label',
        placeholder: 'History picker placeholder',
        description: 'History picker description',
        required: true,
        options: [
            { label: 'Option 1', value: 'option1' },
            { label: 'Option 2', value: 'option2' },
            { label: 'Option 3', value: 'option3' }
        ],
        valueProp: 'value',
        labelProp: 'label',
        indeterminate: false,
        hideFieldUnderline: false,
        hideLabel: false,
        align: 'start',
        appearance: 'legacy'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputAccordion: StudioFormConfig = {
    studioInputType: 'ACCORDION_GROUP',
    studioInputName: 'Section',
    studioIcon: 'web_asset',
    studioTemp: false,
    studioInputId: '',
    type: 'dnd-accordion',
    key: 'childInputElements',
    studioFieldGroup: [],
    isEditable: false,
    fieldArray: {
        fieldGroup: [
            { 
                name: 'RADIO_BUTTON',
                expressions: {
                    hide: (field: FormlyFieldConfig) => {
                         return field.model.studioInputType !== "RADIO_BUTTON"
                    }
                },
               
                "props": {
                    "label": "Radio button",
                }, 
                fieldGroup: [
                    {
                        ...studioFormInputRadio,
                        key: 'studioInputType', 
                        type: 'input',
                        defaultValue: 'RADIO_BUTTON',
                        hooks: {
                            onInit: (field: StudioFormConfig) => {
                                console.log(field)
                                field.studioInputId = `${createId().replace('-', '')}`;
                            }
                        },
                    }
                ]
             },
             { 
                name: 'CHECKBOX',
                expressions: {
                    hide: (field: FormlyFieldConfig) => {
                         return field.model.studioInputType !== "CHECKBOX"
                     },
                },
                "props": {
                    "label": "Checkbox",
                }, 
                fieldGroup: [
                    {
                        ...studioFormInputCheckbox,
                        key: 'studioInputType', 
                        type: 'input',
                        defaultValue: 'CHECKBOX',
                        hooks: {
                            onInit: (field: StudioFormConfig) => {
                                console.log(field)
                                field.studioInputId = `${createId().replace('-', '')}`;
                            }
                        },
                    }
                ]
             },
        ]
    },
    props: {
        disabled: true,
        label: 'Accordion group label',
        required: true,
        appearance: 'legacy',
    },
    wrappers: ['field-generator-wrapper'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputTables: StudioFormConfig = {
    studioInputType: 'TABLE_SELECT',
    studioInputName: 'Tables',
    studioIcon: 'table_rows',
    studioInputId: '',
    studioTemp: false,
    isEditable: true,
    type: 'input',
    key: 'input',
    props: {
        label: 'Tables',
        required: false,
        placeholder: 'Select table',
        description: 'Select table',
        disabled: true,
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: ['TABLE_SELECT_STEP_NODE']
}

export const studioFormInputColumns: StudioFormConfig = {
    studioInputType: 'COLUMN_SELECT',
    studioInputName: 'Columns',
    studioIcon: 'view_column',
    studioInputId: '',
    studioTemp: false,
    isEditable: true,
    type: 'input',
    key: 'input',
    props: {
        label: 'Columns',
        required: false,
        placeholder: 'Select column',
        description: 'Select column',
        disabled: true,
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: ['COLUMN_SELECT_STEP_NODE']
}

export const studioFormEntitySelect: StudioFormConfig = {
    studioInputType: 'ENTITY_SELECT',
    studioInputName: 'Entity Select',
    studioIcon: 'list',
    studioInputId: '',
    studioTemp: false,
    isEditable: true,
    type: 'input',
    key: 'input',
    props: {
        label: 'Entity Select',
        required: false,
        placeholder: 'Select entity',
        description: 'Select entity',
        disabled: true,
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormOAuthInput: StudioFormConfig = {
    studioInputType: 'OAUTH',
    studioInputName: 'OAuth',
    studioIcon: 'shield',
    studioInputId: '',
    studioTemp: false,
    isEditable: true,
    type: 'input',
    key: 'input',
    props: {
        label: 'OAuth',
        required: false,
        placeholder: 'Select',
        description: 'Select',
        disabled: true,
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'OAUTH_STEP_NODE'
    ]
}

export const studioFormInputHistoryPicker: StudioFormConfig = {
    studioInputType: 'HISTORY_PICKER',
    studioInputName: 'History Picker',
    studioIcon: 'manage_history',
    studioInputId: '',
    studioTemp: false,
    isEditable: true,
    type: 'input',
    key: 'input',
    props: {
        label: 'Replication start date',
        required: false,
        placeholder: 'Select date',
        description: 'Select date',
        disabled: true,
        appearance: 'outline'
    },
    wrappers: ['field-generator-wrapper', 'form-field'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const studioFormInputFixedValuesSlider: StudioFormConfig = {
    studioInputType: 'FIXED_VALUES_SLIDER',
    studioInputName: 'Slider',
    studioIcon: 'linear_scale',
    studioInputId: '',
    studioTemp: false,
    "type": "slider",
    isEditable: true,
    key: 'input',
    props: {
        "label": "Replication Frequency",
        "placeholder": "Slider placeholder",
        "required": true,
        "description": "Slider description",
        "appearance": "",
        "thumbLabel": true,
        "tickInterval": 1,
        "step": 1,
        "min": 1,
        "max": 24,
        disabled: true,
        displayWith: formatFormlySliderFieldLabel,
        "sliderOptions": {
            "showTicks": true,
            "hideLimitLabels": true,
            disabled: true,
            "stepsArray": [
              
            ]
        }
    },
    wrappers: ['field-generator-wrapper', 'wrapper'],
    allowedStepTypes: [
        'FIRST_STEP_NODE',
        'NORMAL_STEP_NODE'
    ]
}

export const StudioFormInputElementTypes: StudioFormConfig[] = [
    studioFormInputIntegrationName,
    studioFormInputText,
    studioFormInputTextArea,
    studioFormInputNumber,
    studioFormInputRadio,
    studioFormInputCheckbox,
    studioFormInputDropdownList,
    studioFormInputAccordion,
    studioFormInputHistoryPicker,
    studioFormInputFixedValuesSlider,
    studioFormEntitySelect,
    studioFormOAuthInput,
    studioFormInputTables,
    studioFormInputColumns,
];

export const StudioFormInputTypesMap = () => {
    const map = new Map();
    StudioFormInputElementTypes.forEach((element) => {
        map.set(element.studioInputType, element);
    });
    return map;
}
